import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { mergeRegister } from "@lexical/utils";
import {
  $createParagraphNode,
  $getNodeByKey,
  $getSelection,
  $isNodeSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_LOW,
  COMMAND_PRIORITY_NORMAL,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ENTER_COMMAND,
} from "lexical";
import { useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import type { MomentFormMoment } from "../../../../../../moment/types/moment-form-moment";
import { useLexicalContext } from "../../../../lexical-context";
import { $createPollNode, $isPollNode } from "../../../../nodes/poll/helpers";
import {
  DELETE_NODE_COMMAND,
  INSERT_POLL_COMMAND,
  MODIFY_POLL_COMMAND,
  OPEN_EDIT_POLL_COMMAND,
} from "../../../../types/commands";
import { $insertNode } from "../../../../utils/insert-node";

export function usePollButtonCommands(): void {
  const { getValues, setValue } = useFormContext<Pick<MomentFormMoment, "poll_ids">>();
  const [editor] = useLexicalComposerContext();
  const {
    editorModal: { handleOpen },
  } = useLexicalContext();

  const insertParagraphIfAtEnd = useCallback((event: KeyboardEvent) => {
    const selection = $getSelection();
    if (!$isNodeSelection(selection)) {
      return false;
    }

    const nodes = selection.getNodes();
    if (nodes.length !== 1) {
      return false;
    }

    const node = nodes[0];
    if (!$isPollNode(node)) {
      return false;
    }

    const noSiblingAfter = node.getNextSibling() === null;

    if (noSiblingAfter) {
      const newParagraph = $createParagraphNode();
      node.insertAfter(newParagraph);
      newParagraph.select();

      event.preventDefault();
      return true;
    }

    return false;
  }, []);

  useEffect(
    () =>
      mergeRegister(
        // Inserts a new PollNode into the document
        editor.registerCommand(
          INSERT_POLL_COMMAND,
          ({ poll_id, answer_type }) => {
            editor.update(() => {
              $insertNode($createPollNode(editor._headless, poll_id, answer_type));
            });
            return true;
          },
          COMMAND_PRIORITY_EDITOR,
        ),
        editor.registerCommand(
          OPEN_EDIT_POLL_COMMAND,
          ({ nodeKey }) => {
            handleOpen({ type: "poll", data: { mode: "edit", key: nodeKey } });
            return true;
          },
          COMMAND_PRIORITY_EDITOR,
        ),
        editor.registerCommand(
          MODIFY_POLL_COMMAND,
          ({ nodeKey, poll_id, answer_type }) => {
            editor.update(() => {
              const node = $getNodeByKey(nodeKey);

              if ($isPollNode(node)) {
                node.setAnswerType(answer_type);
                node.setPollId(poll_id);
                node.refresh();
              }
            });
            return true;
          },
          COMMAND_PRIORITY_EDITOR,
        ),
        editor.registerCommand(
          DELETE_NODE_COMMAND,
          (nodeKey) => {
            const node = $getNodeByKey(nodeKey);

            if ($isPollNode(node)) {
              setValue(
                "poll_ids",
                (getValues("poll_ids") ?? []).filter((id) => id !== node.getPollId()),
              );
            }

            return false;
          },
          COMMAND_PRIORITY_NORMAL,
        ),
        editor.registerCommand(
          KEY_ARROW_DOWN_COMMAND,
          insertParagraphIfAtEnd,
          COMMAND_PRIORITY_LOW,
        ),
        editor.registerCommand(KEY_ENTER_COMMAND, insertParagraphIfAtEnd, COMMAND_PRIORITY_LOW),
      ),
    [editor, handleOpen, setValue, getValues, insertParagraphIfAtEnd],
  );
}
